import { cs } from '../../lib/currency';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { DollarSign, AlertTriangle, CheckCircle } from 'lucide-react';
import { DIVISION_PREFERENCES } from './constants';

export const ParentTournamentRegisterDialog = ({
  open,
  onOpenChange,
  selectedTournament,
  formData,
  setFormData,
  eligibilityInfo,
  locations = [],
  getUnregisteredPlayers,
  getAvailableCategoriesForPlayer,
  getEligibilityForCategory,
  getPlayUpCategory,
  handlePlayerSelect,
  handleRegister,
}) => {
  return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Register for {selectedTournament?.name}</DialogTitle>
            <DialogDescription>
              Register your child for this tournament
            </DialogDescription>
          </DialogHeader>

          {/* Initial Payment Info - Always shown */}
          <div className={`rounded-lg p-4 -mt-2 ${selectedTournament?.initial_payment_amount > 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${selectedTournament?.initial_payment_amount > 0 ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                <DollarSign className={`h-5 w-5 ${selectedTournament?.initial_payment_amount > 0 ? 'text-emerald-600' : 'text-slate-500'}`} />
              </div>
              <div>
                <p className={`font-semibold ${selectedTournament?.initial_payment_amount > 0 ? 'text-emerald-800' : 'text-slate-700'}`}>
                  {selectedTournament?.initial_payment_amount > 0 ? 'Initial Payment Required' : 'Tournament Fee'}
                </p>
                <p className={`text-2xl font-bold ${selectedTournament?.initial_payment_amount > 0 ? 'text-emerald-700' : 'text-slate-600'}`}>
                  {cs()}{(selectedTournament?.initial_payment_amount || 0).toFixed(2)}
                </p>
                <p className={`text-xs ${selectedTournament?.initial_payment_amount > 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {selectedTournament?.initial_payment_amount > 0 
                    ? 'Due upon registration approval. Final cost may vary based on actual expenses.'
                    : 'No initial payment required for this tournament.'}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 py-4">
            <div>
              <Label>Select Child</Label>
              <Select
                value={formData.player_id}
                onValueChange={handlePlayerSelect}
              >
                <SelectTrigger data-testid="player-select">
                  <SelectValue placeholder="Select a child" />
                </SelectTrigger>
                <SelectContent>
                  {getUnregisteredPlayers(selectedTournament || { id: '', age_categories: [] }).map(player => (
                    <SelectItem key={player.id} value={player.id}>
                      {player.full_name || player.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Show player age info if available */}
            {eligibilityInfo && (
              <div className="bg-slate-50 p-3 rounded-lg border">
                <p className="text-sm font-medium">
                  {eligibilityInfo.player_name} - Current Age: {eligibilityInfo.current_age} years
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  DOB: {eligibilityInfo.date_of_birth} • Cutoff Date: {eligibilityInfo.default_cutoff_date}
                </p>
                {eligibilityInfo.age_at_cutoff && (
                  <p className="text-xs text-blue-600 mt-1">
                    Age at cutoff: {eligibilityInfo.age_at_cutoff} years
                  </p>
                )}
              </div>
            )}

            <div>
              <Label>Age Category</Label>
              <Select
                value={formData.age_category}
                onValueChange={(value) => setFormData({ ...formData, age_category: value, play_up: false, play_up_category: '' })}
              >
                <SelectTrigger data-testid="age-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {/* Only show categories the player hasn't registered for yet */}
                  {(formData.player_id 
                    ? getAvailableCategoriesForPlayer(selectedTournament || { id: '', age_categories: [] }, formData.player_id)
                    : selectedTournament?.age_categories || []
                  ).map(cat => {
                    const eligibility = getEligibilityForCategory(cat);
                    return (
                      <SelectItem key={cat} value={cat}>
                        {cat.toUpperCase()}
                        {eligibility && ` (Age ${eligibility.age_at_cutoff} at cutoff)`}
                        {eligibility?.is_eligible && ' ✓'}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              
              {/* Eligibility warning for selected category */}
              {formData.age_category && getEligibilityForCategory(formData.age_category)?.warning && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  {getEligibilityForCategory(formData.age_category).warning}
                </div>
              )}
            </div>

            {/* Play Up Option */}
            {formData.player_id && formData.age_category && getPlayUpCategory() && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="play_up"
                    checked={formData.play_up}
                    onCheckedChange={(checked) => {
                      const playUpCat = getPlayUpCategory();
                      setFormData({ 
                        ...formData, 
                        play_up: checked,
                        play_up_category: checked ? playUpCat : ''
                      });
                    }}
                    data-testid="play-up-checkbox"
                  />
                  <div className="flex-1">
                    <label htmlFor="play_up" className="text-sm font-medium text-blue-800 cursor-pointer">
                      Also register for {getPlayUpCategory()?.toUpperCase()} (Play Up)
                    </label>
                    <p className="text-xs text-blue-600 mt-1">
                      Your child is eligible for <strong>{formData.age_category.toUpperCase()}</strong>. 
                      Check this box to also compete in the higher <strong>{getPlayUpCategory()?.toUpperCase()}</strong> age group.
                    </p>
                    {formData.play_up && (
                      <div className="mt-2 p-2 bg-green-100 rounded border border-green-200">
                        <p className="text-xs text-green-800 font-medium">
                          <CheckCircle className="h-3 w-3 inline mr-1" />
                          Will register for BOTH categories:
                        </p>
                        <ul className="text-xs text-green-700 ml-4 mt-1">
                          <li>• {formData.age_category.toUpperCase()} (base category)</li>
                          <li>• {getPlayUpCategory()?.toUpperCase()} (play-up category)</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Training Location - Required */}
            <div>
              <Label className="text-slate-700">
                Current Training Location <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.training_location_id}
                onValueChange={(value) => setFormData({ ...formData, training_location_id: value })}
              >
                <SelectTrigger data-testid="training-location-select" className={!formData.training_location_id ? 'border-amber-300' : ''}>
                  <SelectValue placeholder="Select your training location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Select where your child currently trains
              </p>
            </div>

            {/* Division Preference - Show only for LOCAL Competition/Recreational tournaments (not travel, not league) */}
            {selectedTournament?.location_type !== 'out_of_state' && 
             selectedTournament?.tournament_style !== 'league' && (
              <div>
                <Label>Division Preference</Label>
                <Select
                  value={formData.division_preference}
                  onValueChange={(value) => setFormData({ ...formData, division_preference: value })}
                >
                  <SelectTrigger data-testid="division-preference-select">
                    <SelectValue placeholder="Select division preference" />
                  </SelectTrigger>
                  <SelectContent>
                    {DIVISION_PREFERENCES.map(div => (
                      <SelectItem key={div.value} value={div.value}>
                        {div.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  This is your preference only. The coach will make the final decision on division and team placement.
                </p>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox
                id="can_travel"
                checked={formData.can_travel}
                onCheckedChange={(checked) => setFormData({ ...formData, can_travel: checked })}
              />
              <label htmlFor="can_travel" className="text-sm">
                Can travel to tournament location
              </label>
            </div>

            <div>
              <Label>Availability</Label>
              <Select
                value={formData.availability_status}
                onValueChange={(value) => setFormData({ ...formData, availability_status: value })}
              >
                <SelectTrigger data-testid="availability-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Available for all dates</SelectItem>
                  <SelectItem value="partial">Partially available</SelectItem>
                  <SelectItem value="no">Not available</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.availability_status === 'partial' && (
              <div>
                <Label>Unavailable Dates (comma-separated, e.g., 2025-03-15, 2025-03-16)</Label>
                <Textarea
                  value={formData.unavailable_dates.join(', ')}
                  onChange={(e) => setFormData({
                    ...formData,
                    unavailable_dates: e.target.value.split(',').map(d => d.trim()).filter(d => d)
                  })}
                  placeholder="Enter dates player is NOT available"
                  data-testid="unavailable-dates-input"
                />
              </div>
            )}

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional information..."
                data-testid="notes-input"
              />
            </div>

            <div className="flex items-start space-x-2 pt-4 border-t">
              <Checkbox
                id="consent"
                checked={formData.parent_consent}
                onCheckedChange={(checked) => setFormData({ ...formData, parent_consent: checked })}
                data-testid="consent-checkbox"
              />
              <label htmlFor="consent" className="text-sm leading-tight">
                I, as the parent/guardian, consent to my child's participation in this tournament. 
                I have read and agree to the{' '}
                <a 
                  href="/terms-of-service" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('/terms-of-service', '_blank');
                  }}
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a 
                  href="/privacy-policy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('/privacy-policy', '_blank');
                  }}
                >
                  Privacy Policy
                </a>.
                I understand that an initial payment of {cs()}{selectedTournament?.initial_payment_amount || 0} 
                will be required if selected, and the final cost may vary based on actual expenses.
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRegister}
              disabled={!formData.player_id || !formData.age_category || !formData.parent_consent}
              data-testid="submit-registration-btn"
            >
              Submit Registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
};

export default ParentTournamentRegisterDialog;
